import { useEffect, useState, useCallback } from "react";
import { api, errText } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";import { Users, BookOpen, Award, Video, Download, UsersRound, Plus } from "lucide-react";

const STAGES = ["applied", "interview", "offered", "hired"];

function StatCard({ icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3">
      <span className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center shrink-0">{icon}</span>
      <div>
        <p className="text-2xl font-heading font-extrabold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );
}

function AppRow({ a, onUpdate }) {
  const { t, pick } = useLang();
  const [interviewAt, setInterviewAt] = useState(a.interview_at || "");
  const [hiredType, setHiredType] = useState(a.hired_type || "volunteer");

  const patch = async (body) => {
    try { await api.patch(`/admin/applications/${a.id}`, body); onUpdate(); toast.success("Updated"); }
    catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };

  return (
    <div data-testid={`admin-app-row-${a.id}`} className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div>
          <p className="font-semibold text-slate-900">{a.user_name} <span className="text-slate-400 font-normal text-sm">· {a.city} · {a.phone}</span></p>
          <p className="text-sm text-slate-500">{pick(a.track_title)} · {t(a.preference)} · {a.certificate_id}</p>
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider bg-teal-100 text-teal-800 px-3 py-1.5 rounded-full">
          {t(a.status)}{a.status === "hired" && a.hired_type ? ` — ${t(a.hired_type)}` : ""}
        </span>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {a.status === "applied" && (
          <>
            <input data-testid={`interview-datetime-${a.id}`} type="datetime-local" value={interviewAt} onChange={(e) => setInterviewAt(e.target.value)}
              className="h-10 px-3 rounded-lg border border-slate-300 text-sm" />
            <button data-testid={`schedule-interview-btn-${a.id}`} onClick={() => patch({ status: "interview", interview_at: interviewAt })}
              disabled={!interviewAt} className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold disabled:opacity-40">
              Schedule {t("interview")}
            </button>
          </>
        )}
        {a.status === "interview" && (
          <button data-testid={`make-offer-btn-${a.id}`} onClick={() => patch({ status: "offered" })}
            className="h-10 px-4 rounded-lg bg-teal-600 text-white text-sm font-semibold">Move to {t("offered")}</button>
        )}
        {a.status === "offered" && (
          <>
            <select data-testid={`hired-type-select-${a.id}`} value={hiredType} onChange={(e) => setHiredType(e.target.value)}
              className="h-10 px-3 rounded-lg border border-slate-300 text-sm bg-white">
              <option value="volunteer">{t("volunteer")}</option>
              <option value="intern">{t("intern")}</option>
              <option value="fulltime">{t("fulltime")}</option>
            </select>
            <button data-testid={`mark-hired-btn-${a.id}`} onClick={() => patch({ status: "hired", hired_type: hiredType })}
              className="h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold">Mark {t("hired")}</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("all");
  const [trends, setTrends] = useState([]);
  const [attSummary, setAttSummary] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [students, setStudents] = useState([]);
  const [cohortForm, setCohortForm] = useState({ name: "", track_id: "", ids: [] });
  const { t, pick } = useLang();

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/applications").then((r) => setApps(r.data)).catch(() => {});
    api.get("/admin/trends").then((r) => setTrends(r.data)).catch(() => {});
    api.get("/admin/attendance-summary").then((r) => setAttSummary(r.data)).catch(() => {});
    api.get("/admin/cohorts").then((r) => setCohorts(r.data)).catch(() => {});
    api.get("/admin/students").then((r) => setStudents(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async (kind) => {
    try {
      const { data } = await api.get(`/admin/export/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `fscd_${kind}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const createCohort = async () => {
    try {
      const { data } = await api.post("/admin/cohorts", { name: cohortForm.name, track_id: cohortForm.track_id, student_ids: cohortForm.ids });
      toast.success(`Cohort created — ${data.newly_enrolled} student(s) newly enrolled`);
      setCohortForm({ name: "", track_id: "", ids: [] });
      load();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
  };

  if (!stats) return <p className="text-slate-500">Loading…</p>;
  const shown = filter === "all" ? apps : apps.filter((a) => a.status === filter);
  const trend = trends;
  const funnelData = ["applied", "interview", "offered", "hired"].map((s) => ({ stage: t(s), count: stats.funnel[s] }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">{t("adminDashboard")}</h1>
        <div className="flex gap-2 flex-wrap">
          {["enrollments", "certificates", "applications", "attendance"].map((k) => (
            <button key={k} data-testid={`export-${k}-btn`} onClick={() => exportCsv(k)}
              className="h-10 px-4 rounded-xl border border-slate-200 bg-white text-slate-600 text-xs font-semibold flex items-center gap-1.5 hover:border-teal-400 hover:text-teal-800 transition-colors">
              <Download size={14} /> {k[0].toUpperCase() + k.slice(1)} CSV
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={<Users size={20} />} label="Trainees" value={stats.trainees} testid="stat-trainees" />
        <StatCard icon={<BookOpen size={20} />} label="Enrollments" value={stats.enrollments} testid="stat-enrollments" />
        <StatCard icon={<Award size={20} />} label="Certificates" value={stats.certificates} testid="stat-certificates" />
        <StatCard icon={<Video size={20} />} label="Pending videos" value={stats.pending_videos} testid="stat-pending-videos" />
      </div>

      <section className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-heading font-bold text-slate-900">Live Class Attendance</h2>
          <span className="text-[10px] font-semibold uppercase tracking-wider bg-teal-50 text-teal-700 px-2.5 py-1 rounded-full">Engagement per class · export via Attendance CSV</span>
        </div>
        {attSummary.length === 0 ? <p className="text-sm text-slate-400">No live classes yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="attendance-summary-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-3">Class</th><th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Joined</th><th className="py-2 pr-3">Enrolled</th><th className="py-2">Attendance</th>
                </tr>
              </thead>
              <tbody>
                {attSummary.map((r) => (
                  <tr key={r.id} data-testid={`attendance-row-${r.id}`} className="border-b border-slate-50">
                    <td className="py-2.5 pr-3 font-medium text-slate-700">{r.course}{r.series_id && <span className="ml-1.5 text-[9px] font-bold uppercase text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded-full">Weekly</span>}</td>
                    <td className="py-2.5 pr-3 text-slate-500">{r.date} · {r.time}</td>
                    <td className="py-2.5 pr-3 text-slate-700 font-semibold">{r.attendees}</td>
                    <td className="py-2.5 pr-3 text-slate-500">{r.enrolled ?? "—"}</td>
                    <td className="py-2.5">
                      {r.rate === null ? <span className="text-slate-400 text-xs">Not course-linked</span> : (
                        <span className="flex items-center gap-2">
                          <span className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden"><span className="block h-full bg-teal-600" style={{ width: `${Math.min(r.rate, 100)}%` }} /></span>
                          <span className={`text-xs font-bold ${r.rate >= 60 ? "text-emerald-600" : r.rate >= 30 ? "text-amber-600" : "text-red-500"}`}>{r.rate}%</span>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="grid lg:grid-cols-2 gap-4">
        <section className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-bold text-slate-900">Enrollment Trend</h2>
            <span className="text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full">Live data · cumulative 8 weeks</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={trend} margin={{ left: -20, right: 8, top: 4 }}>
              <defs>
                <linearGradient id="gEnr" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0F766E" stopOpacity={0.35} /><stop offset="100%" stopColor="#0F766E" stopOpacity={0} /></linearGradient>
                <linearGradient id="gCert" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F59E0B" stopOpacity={0.35} /><stop offset="100%" stopColor="#F59E0B" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Area type="monotone" dataKey="enrollments" stroke="#0F766E" strokeWidth={2.5} fill="url(#gEnr)" name="Enrollments" />
              <Area type="monotone" dataKey="certificates" stroke="#F59E0B" strokeWidth={2.5} fill="url(#gCert)" name="Certificates" />
            </AreaChart>
          </ResponsiveContainer>
        </section>
        <section className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm">
          <h2 className="font-heading font-bold text-slate-900 mb-3">Hiring Funnel</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={funnelData} margin={{ left: -20, right: 8, top: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="stage" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Bar dataKey="count" fill="#0F766E" radius={[8, 8, 0, 0]} maxBarSize={48} name="Candidates" />
            </BarChart>
          </ResponsiveContainer>
        </section>
      </div>

      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
        <h2 className="font-heading font-bold text-lg text-slate-900">Tracks</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400 uppercase tracking-wider">
              <th className="py-2 pr-4">Track</th><th className="py-2 pr-4">Enrolled</th><th className="py-2 pr-4">Avg progress</th><th className="py-2">Certified</th>
            </tr></thead>
            <tbody>
              {stats.per_track.map((r) => (
                <tr key={r.track_id} className="border-t border-slate-100" data-testid={`track-stats-${r.track_id}`}>
                  <td className="py-2 pr-4 font-medium text-slate-800">{pick(r.title)}</td>
                  <td className="py-2 pr-4">{r.enrolled}</td>
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-teal-600" style={{ width: `${r.avg_progress_pct}%` }} /></div>
                      {r.avg_progress_pct}%
                    </div>
                  </td>
                  <td className="py-2">{r.certified}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200/80 p-5 space-y-4 shadow-sm" data-testid="cohorts-section">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2"><UsersRound size={18} /> Cohorts</h2>
          <span className="text-xs text-slate-400">{cohorts.length} cohort(s)</span>
        </div>
        {cohorts.map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-3 border border-slate-100 rounded-xl px-4 py-3" data-testid={`cohort-row-${c.id}`}>
            <div><p className="font-semibold text-slate-800 text-sm">{c.name}</p><p className="text-xs text-slate-400">{pick(c.track_title)} · created {new Date(c.created_at).toLocaleDateString()}</p></div>
            <span className="text-xs font-semibold bg-teal-50 text-teal-800 px-3 py-1.5 rounded-full">{c.student_ids.length} students</span>
          </div>
        ))}
        <div className="border-t border-slate-100 pt-4 grid sm:grid-cols-2 gap-3">
          <input data-testid="cohort-name-input" placeholder="Cohort name (e.g. Jaipur Batch A — July)" value={cohortForm.name}
            onChange={(e) => setCohortForm({ ...cohortForm, name: e.target.value })}
            className="h-11 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600" />
          <select data-testid="cohort-track-select" value={cohortForm.track_id} onChange={(e) => setCohortForm({ ...cohortForm, track_id: e.target.value })}
            className="h-11 px-3 rounded-xl border border-slate-200 text-sm bg-white">
            <option value="">Assign course…</option>
            {stats.per_track.map((r) => <option key={r.track_id} value={r.track_id}>{pick(r.title)}</option>)}
          </select>
          <div className="sm:col-span-2 flex flex-wrap gap-2">
            {students.map((s) => {
              const on = cohortForm.ids.includes(s.id);
              return (
                <button key={s.id} data-testid={`cohort-student-${s.id}`}
                  onClick={() => setCohortForm({ ...cohortForm, ids: on ? cohortForm.ids.filter((x) => x !== s.id) : [...cohortForm.ids, s.id] })}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-teal-800 text-white border-teal-800" : "bg-white text-slate-500 border-slate-200 hover:border-teal-400"}`}>
                  {s.name}
                </button>
              );
            })}
          </div>
          <button data-testid="create-cohort-btn" onClick={createCohort} disabled={!cohortForm.name.trim() || !cohortForm.track_id || cohortForm.ids.length === 0}
            className="h-11 px-6 rounded-xl bg-teal-800 text-white text-sm font-semibold disabled:opacity-40 flex items-center gap-1.5 w-fit"><Plus size={15} /> Create Cohort & Enroll</button>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-heading font-bold text-lg text-slate-900">Hiring funnel</h2>
          <div className="flex gap-1 flex-wrap">
            {["all", ...STAGES].map((s) => (
              <button key={s} data-testid={`funnel-filter-${s}`} onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filter === s ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
                {s === "all" ? "All" : `${t(s)} (${stats.funnel[s]})`}
              </button>
            ))}
          </div>
        </div>
        {shown.length === 0 && <p className="text-slate-400 bg-white rounded-2xl border border-slate-200 p-6 text-center" data-testid="no-candidates-msg">No candidates</p>}
        {shown.map((a) => <AppRow key={a.id} a={a} onUpdate={load} />)}
      </section>
    </div>
  );
}
